// src/components/AddProject.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import { api } from "../api";

const initial = {
  name: "",
  category: "",
  clientName: "",
  clientAddress: "",

  // Primary email
  clientEmail: "",

  // Additional emails
  clientEmails: [],

  clientContactNumber: "",
  startDate: "",
  completionDate: "",
};

const categories = [
  { name: "USA", permission: "USA" },
  { name: "CANADA", permission: "CANADA" },
  { name: "Arch.ind", permission: "ArchInd" },
  { name: "Gruda", permission: "Gruda" },
  { name: "DC", permission: "DC" },
  { name: "ProjectData", permission: "ProjectData" },
  { name: "Interior", permission: "Interior" },
  { name: "BD & M", permission: "BDM" },
  { name: "Finance", permission: "Finance" },
];

export default function AddProject() {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.authUser);

  const [permissions, setPermissions] = useState({});
  const [permissionLoaded, setPermissionLoaded] = useState(false);

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [projects, setProjects] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [catOpen, setCatOpen] = useState({});
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [searchApplied, setSearchApplied] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [ep, setEp] = useState({ ...initial, _id: "" });
  const [editSaving, setEditSaving] = useState(false);

  const clean = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();
  const isAdmin = clean(authUser?.role) === "admin";

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addClientEmail = () => {
    const email = prompt("Enter email address");

    if (!email) return;

    const cleanEmail = email.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      alert("Invalid email");
      return;
    }

    setForm((old) => {
      const emails = [old.clientEmail, ...old.clientEmails]
        .filter(Boolean)
        .map((e) => e.toLowerCase());

      if (emails.includes(cleanEmail)) {
        alert("Email already exists");
        return old;
      }

      return {
        ...old,
        clientEmails: [...old.clientEmails, cleanEmail],
      };
    });
  };

  const removeClientEmail = (email) => {
    setForm((old) => ({
      ...old,
      clientEmails: old.clientEmails.filter((item) => item !== email),
    }));
  };
  const hasProjectCategoryPermission = (permission) => {
    if (isAdmin) return true;
    return Boolean(permissions?.addProject?.[permission]);
  };

  const canCreateProject = useMemo(() => {
    if (isAdmin) return true;
    return Boolean(permissions?.addProject?.create);
  }, [isAdmin, permissions]);

  const canViewProject = useMemo(() => {
    if (isAdmin) return true;
    return Boolean(permissions?.addProject?.view);
  }, [isAdmin, permissions]);

  const allowedCategories = useMemo(() => {
    if (isAdmin) return categories;
    if (!canViewProject) return [];

    return categories.filter((cat) =>
      hasProjectCategoryPermission(cat.permission),
    );
  }, [permissions, isAdmin, canViewProject]);

  const allowedCategoryNames = useMemo(
    () => allowedCategories.map((c) => c.name),
    [allowedCategories],
  );

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await api.get("/roles/my-permissions");
        setPermissions(res.data?.permissions || {});
      } catch (err) {
        console.error("Permission fetch failed:", err);
        setPermissions({});
      } finally {
        setPermissionLoaded(true);
      }
    };

    fetchPermissions();
  }, []);

  const validate = () => {
    if (!canCreateProject)
      return "You do not have permission to create project";
    if (!form.name.trim()) return "Project name is required";
    if (!form.category.trim()) return "Category is required";

    const allowed = allowedCategoryNames.some(
      (cat) => clean(cat) === clean(form.category),
    );

    if (!isAdmin && !allowed) {
      return "You do not have permission for this project category";
    }

    if (!form.clientName.trim()) return "Client name is required";
    if (!form.clientAddress.trim()) return "Client address is required";
    if (!form.clientEmail.trim()) return "Client email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.clientEmail.trim()))
      return "Enter a valid client email";
    if (!form.clientContactNumber.trim())
      return "Client contact number is required";
    if (!form.startDate) return "Start date is required";

    if (form.completionDate) {
      const s = new Date(form.startDate);
      const c = new Date(form.completionDate);
      if (c < s) return "Completion date cannot be before Start date";
    }

    return "";
  };

  const validateEdit = () => {
    if (!ep.name.trim()) return "Project name is required";
    if (!ep.category.trim()) return "Category is required";

    const allowed = allowedCategoryNames.some(
      (cat) => clean(cat) === clean(ep.category),
    );

    if (!isAdmin && !allowed) {
      return "You do not have permission for this project category";
    }

    if (!ep.clientName.trim()) return "Client name is required";
    if (!ep.clientAddress.trim()) return "Client address is required";
    if (!ep.clientEmail.trim()) return "Client email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ep.clientEmail.trim()))
      return "Enter a valid client email";
    if (!ep.clientContactNumber.trim())
      return "Client contact number is required";
    if (!ep.startDate) return "Start date is required";

    if (ep.completionDate) {
      const s = new Date(ep.startDate);
      const c = new Date(ep.completionDate);
      if (c < s) return "Completion date cannot be before Start date";
    }

    return "";
  };

  const groupByCategory = (items) => {
    const map = {};
    for (const p of items) {
      const cat = String(p.category || "Uncategorized").trim();
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    }
    return map;
  };

  const fetchProjects = async () => {
    try {
      setListLoading(true);
      const { data } = await api.get("/projects");
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || "")),
          )
        : [];
      setProjects(sorted);
    } catch (e) {
      console.error("Fetch projects error:", e);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const onRefresh = () => fetchProjects();
    window.addEventListener("projects:refresh", onRefresh);
    return () => window.removeEventListener("projects:refresh", onRefresh);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    const err = validate();
    if (err) return setMsg({ type: "error", text: err });
    const payload = {
      name: form.name.trim(),
      category: form.category.trim(),
      clientName: form.clientName.trim(),
      clientAddress: form.clientAddress.trim(),

      clientEmail: form.clientEmail.trim().toLowerCase(),

      clientEmails: form.clientEmails
        .map((email) =>
          String(email || "")
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),

      clientContactNumber: form.clientContactNumber.trim(),

      startDate: form.startDate,

      completionDate: form.completionDate || null,
    };

    try {
      setLoading(true);
      const { data: created } = await api.post("/projects", payload);

      setMsg({ type: "success", text: "Project created successfully" });
      setForm(initial);
      await fetchProjects();

      window.dispatchEvent(
        new CustomEvent("projects:refresh", { detail: created }),
      );
    } catch (err) {
      setMsg({
        type: "error",
        text:
          err.response?.data?.message || err.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (proj) => {
    setEp({
      _id: proj._id,
      name: proj.name || "",
      category: proj.category || "",
      clientName: proj.clientName || "",
      clientAddress: proj.clientAddress || "",
      clientEmail: proj.clientEmail || "",
      clientContactNumber: proj.clientContactNumber || "",
      startDate: proj.startDate
        ? new Date(proj.startDate).toISOString().slice(0, 10)
        : "",
      completionDate: proj.completionDate
        ? new Date(proj.completionDate).toISOString().slice(0, 10)
        : "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const err = validateEdit();
    if (err) return alert(err);

    try {
      setEditSaving(true);

      const { data: updated } = await api.patch(`/projects/${ep._id}`, {
        name: ep.name.trim(),
        category: ep.category.trim(),
        clientName: ep.clientName.trim(),
        clientAddress: ep.clientAddress.trim(),
        clientEmail: ep.clientEmail.trim().toLowerCase(),
        clientContactNumber: ep.clientContactNumber.trim(),
        startDate: ep.startDate,
        completionDate: ep.completionDate || null,
      });

      setEditOpen(false);
      await fetchProjects();

      window.dispatchEvent(
        new CustomEvent("projects:refresh", { detail: updated }),
      );

      alert("Project updated ✅");
    } catch (e) {
      alert(e.response?.data?.message || e.message || "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };

  const normalize = (s) => String(s || "").toLowerCase();

  const matchesQuery = (p, q) => {
    if (!q) return true;
    const n = normalize(q);

    return (
      normalize(p.name).includes(n) ||
      normalize(p.clientName).includes(n) ||
      normalize(p.clientEmail).includes(n) ||
      normalize(p.clientAddress).includes(n)
    );
  };

  const filtered = projects.filter((p) => {
    if (!permissionLoaded) return false;

    const projectCategory = clean(p.category);

    const categoryAllowed =
      isAdmin ||
      allowedCategoryNames.some((cat) => clean(cat) === projectCategory);

    if (!categoryAllowed) return false;

    const catOkay =
      categoryFilter === "All"
        ? true
        : clean(categoryFilter) === projectCategory;

    return catOkay && matchesQuery(p, searchApplied);
  });

  const grouped = groupByCategory(filtered);
  const sortedCats = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
  const totalResults = filtered.length;

  const page = {
    maxWidth: 1100,
    margin: "18px auto",
    padding: 10,
    fontFamily: "Calibri, system-ui, sans-serif",
  };

  const card = {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
    border: "1px solid #ececf5",
    padding: 16,
  };

  const gapY = { display: "grid", gap: 14 };

  const row = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 10,
  };

  const full = { marginBottom: 10 };

  const label = {
    fontWeight: "600",
    marginBottom: 6,
    color: "#2E3267",
    fontSize: 14,
  };

  const input = {
    width: "94%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d7d7e0",
    outline: "none",
    fontSize: 14,
    lineHeight: 1.2,
  };

  const selectInp = { ...input, background: "#fff" };

  const btnRow = { display: "flex", gap: 10, marginTop: 10 };

  const btn = (primary) => ({
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    background: primary ? "#2E3267" : "#f1f1f6",
    color: primary ? "#fff" : "#2E3267",
    fontWeight: "bold",
    fontSize: 14,
  });

  const note = (type) => ({
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${type === "error" ? "#e86" : "#9bd"}`,
    color: type === "error" ? "#7a1f1f" : "#123a5a",
    background: type === "error" ? "#fdecec" : "#eef7ff",
    marginBottom: 8,
    fontSize: 14,
  });

  const accordionHeader = (open) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 8,
    background: open ? "#eef2ff" : "#f8faff",
    border: "1px solid #dbe4ff",
    cursor: "pointer",
    fontSize: 14,
  });

  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    background: "#fff",
    border: "1px solid #d7d7e0",
    color: "#1f2937",
    padding: "4px 8px",
    borderRadius: 999,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ ...page, ...gapY }}>
      <div style={card}>
        <h2 style={{ margin: "0 0 10px 0", color: "#2E3267", fontSize: 20 }}>
          ➕ Add Project
        </h2>

        <p style={{ marginTop: 0, color: "#62658a", fontSize: 14 }}>
          Fill client details and start date. Completion date is optional.
        </p>

        {!permissionLoaded && (
          <div style={note("info")}>Loading permissions...</div>
        )}

        {permissionLoaded && !isAdmin && !canViewProject && (
          <div style={note("error")}>
            You do not have permission to view Add Project.
          </div>
        )}

        {permissionLoaded &&
          !isAdmin &&
          canViewProject &&
          allowedCategories.length === 0 && (
            <div style={note("error")}>
              You do not have permission for any project category.
            </div>
          )}

        {msg.text ? <div style={note(msg.type)}>{msg.text}</div> : null}

        <form onSubmit={handleSubmit}>
          <div style={row}>
            <div>
              <div style={label}>Project Name *</div>
              <input
                style={input}
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g., Phoenix Tower"
                required
                disabled={!permissionLoaded || !canViewProject}
              />
            </div>

            <div>
              <div style={label}>Category *</div>
              <select
                style={selectInp}
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
                required
                disabled={
                  !permissionLoaded ||
                  !canViewProject ||
                  (!isAdmin && allowedCategories.length === 0)
                }
              >
                <option value="">Select category</option>

                {allowedCategories.map((cat) => (
                  <option key={cat.name} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={row}>
            <div>
              <div style={label}>Client Name *</div>
              <input
                style={input}
                value={form.clientName}
                onChange={(e) => setField("clientName", e.target.value)}
                placeholder="Acme Developments"
                required
                disabled={!permissionLoaded || !canViewProject}
              />
            </div>

            <div>
              <div style={label}>Client Email *</div>
              <input
                type="email"
                style={input}
                value={form.clientEmail}
                onChange={(e) => setField("clientEmail", e.target.value)}
                placeholder="pm@acme.dev"
                required
                disabled={!permissionLoaded || !canViewProject}
              />
            </div>
          </div>

          <div style={row}>
            <div>
              <div style={label}>Client Contact Number *</div>
              <input
                style={input}
                value={form.clientContactNumber}
                onChange={(e) =>
                  setField("clientContactNumber", e.target.value)
                }
                placeholder="+91 98765 43210"
                required
                disabled={!permissionLoaded || !canViewProject}
              />
            </div>

            <div>
              <div style={label}>Start Date *</div>
              <input
                type="date"
                style={input}
                value={form.startDate}
                onChange={(e) => setField("startDate", e.target.value)}
                required
                disabled={!permissionLoaded || !canViewProject}
              />
            </div>
          </div>

          <div style={full}>
            <div style={label}>Client Address *</div>
            <textarea
              rows={3}
              style={{ ...input, resize: "vertical" }}
              value={form.clientAddress}
              onChange={(e) => setField("clientAddress", e.target.value)}
              placeholder="123 Ring Road, Surat"
              required
              disabled={!permissionLoaded || !canViewProject}
            />
          </div>
          <div style={full}>
            <div style={label}>Additional Client Emails</div>

            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <button type="button" onClick={addClientEmail} style={btn(false)}>
                + Add Email
              </button>
            </div>

            {form.clientEmails.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                {form.clientEmails.map((email) => (
                  <div
                    key={email}
                    style={{
                      background: "#eef2ff",
                      border: "1px solid #c7d2fe",
                      borderRadius: 20,
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span>{email}</span>

                    <button
                      type="button"
                      onClick={() => removeClientEmail(email)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "red",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={full}>
            <div style={label}>Completion Date (optional)</div>
            <input
              type="date"
              style={input}
              value={form.completionDate}
              onChange={(e) => setField("completionDate", e.target.value)}
              disabled={!permissionLoaded || !canViewProject}
            />
          </div>

          <div style={btnRow}>
            <button
              type="submit"
              style={btn(true)}
              disabled={
                loading ||
                !permissionLoaded ||
                !canViewProject ||
                !canCreateProject ||
                (!isAdmin && allowedCategories.length === 0)
              }
            >
              {loading ? "Saving..." : "Create Project"}
            </button>

            <button
              type="button"
              style={btn(false)}
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div style={card}>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginBottom: 8,
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ margin: 0, color: "#2E3267", fontSize: 18 }}>
            Projects (by Category)
          </h3>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              marginLeft: "auto",
              padding: "6px 8px",
              borderRadius: 8,
              border: "1px solid #d7d7e0",
              background: "#fff",
              fontSize: 14,
            }}
            disabled={!permissionLoaded || !canViewProject}
          >
            <option value="All">All</option>

            {allowedCategories.map((cat) => (
              <option key={cat.name} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>

          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSearchApplied(searchText.trim());
            }}
            placeholder="Search by project/client/email/address"
            style={{
              flex: "1 1 240px",
              minWidth: 200,
              padding: "6px 8px",
              borderRadius: 8,
              border: "1px solid #d7d7e0",
              background: "#fff",
              fontSize: 14,
            }}
            disabled={!permissionLoaded || !canViewProject}
          />

          <button
            onClick={() => setSearchApplied(searchText.trim())}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "none",
              background: "#2E3267",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 14,
            }}
            disabled={!permissionLoaded || !canViewProject}
          >
            Search
          </button>

          <button
            onClick={() => {
              setSearchText("");
              setSearchApplied("");
            }}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #d7d7e0",
              background: "#f7f7fb",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
            disabled={!permissionLoaded || !canViewProject}
          >
            Reset
          </button>

          <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 12 }}>
            {listLoading || !permissionLoaded
              ? "Loading..."
              : `Results: ${totalResults}`}
          </span>
        </div>

        {!permissionLoaded ? (
          <div style={{ color: "#6b7280" }}>Loading permissions...</div>
        ) : !canViewProject ? (
          <div style={{ color: "#6b7280" }}>
            You do not have permission to view projects.
          </div>
        ) : listLoading ? (
          <div style={{ color: "#6b7280" }}>Loading projects...</div>
        ) : sortedCats.length === 0 ? (
          <div style={{ color: "#6b7280" }}>
            {searchApplied || categoryFilter !== "All"
              ? "No matching projects."
              : "No projects found for your allowed categories."}
          </div>
        ) : (
          sortedCats.map((cat) => {
            const open = !!catOpen[cat];

            return (
              <div key={cat} style={{ marginBottom: 8 }}>
                <div
                  style={accordionHeader(open)}
                  onClick={() => setCatOpen((o) => ({ ...o, [cat]: !o[cat] }))}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <strong>{cat}</strong>
                    <span style={chip}>{grouped[cat].length} projects</span>
                  </div>
                  <div style={{ fontSize: 12 }}>{open ? "▾" : "▸"}</div>
                </div>

                {open && (
                  <div
                    style={{
                      border: "1px solid #eef2ff",
                      borderTop: "none",
                      padding: 8,
                      borderRadius: "0 0 8px 8px",
                      background: "#fafbff",
                      fontSize: 14,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
                        gap: 8,
                        fontSize: 13,
                        padding: "4px 6px",
                        color: "#475569",
                        fontWeight: 600,
                      }}
                    >
                      <div>Project</div>
                      <div>Client</div>
                      <div>Start</div>
                      <div>Completion</div>
                      <div>Action</div>
                    </div>

                    {grouped[cat].map((p) => (
                      <div
                        key={p._id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
                          gap: 8,
                          alignItems: "center",
                          padding: "6px 6px",
                          borderTop: "1px solid #e5e7eb",
                          background: "#fff",
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {p.clientName || "—"}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {p.clientEmails && p.clientEmails.length > 0
                              ? p.clientEmails.join(", ")
                              : p.clientEmail || "—"}
                          </div>
                        </div>
                        <div>
                          {p.startDate
                            ? new Date(p.startDate).toLocaleDateString("en-GB")
                            : "—"}
                        </div>
                        <div>
                          {p.completionDate
                            ? new Date(p.completionDate).toLocaleDateString(
                                "en-GB",
                              )
                            : "—"}
                        </div>
                        <div>
                          <button
                            onClick={() => openEdit(p)}
                            style={{
                              padding: "6px 10px",
                              borderRadius: 8,
                              border: "1px solid #c7d2fe",
                              background: "#eef2ff",
                              color: "#1e3a8a",
                              cursor: "pointer",
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {editOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
          onClick={() => setEditOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(880px, 96vw)",
              background: "#fff",
              borderRadius: 12,
              padding: 16,
              boxShadow: "0 10px 40px rgba(0,0,0,.2)",
            }}
          >
            <h3 style={{ margin: "0 0 12px", color: "#2E3267" }}>
              Edit Project
            </h3>

            <div style={row}>
              <div>
                <div style={label}>Project Name *</div>
                <input
                  style={input}
                  value={ep.name}
                  onChange={(e) =>
                    setEp((s) => ({ ...s, name: e.target.value }))
                  }
                />
              </div>

              <div>
                <div style={label}>Category *</div>
                <select
                  style={selectInp}
                  value={ep.category}
                  onChange={(e) =>
                    setEp((s) => ({ ...s, category: e.target.value }))
                  }
                >
                  <option value="">Select category</option>
                  {allowedCategories.map((cat) => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={row}>
              <div>
                <div style={label}>Client Name *</div>
                <input
                  style={input}
                  value={ep.clientName}
                  onChange={(e) =>
                    setEp((s) => ({ ...s, clientName: e.target.value }))
                  }
                />
              </div>

              <div>
                <div style={label}>Client Email *</div>
                <input
                  type="email"
                  style={input}
                  value={ep.clientEmail}
                  onChange={(e) =>
                    setEp((s) => ({ ...s, clientEmail: e.target.value }))
                  }
                />
              </div>
            </div>

            <div style={row}>
              <div>
                <div style={label}>Client Contact Number *</div>
                <input
                  style={input}
                  value={ep.clientContactNumber}
                  onChange={(e) =>
                    setEp((s) => ({
                      ...s,
                      clientContactNumber: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <div style={label}>Start Date *</div>
                <input
                  type="date"
                  style={input}
                  value={ep.startDate}
                  onChange={(e) =>
                    setEp((s) => ({ ...s, startDate: e.target.value }))
                  }
                />
              </div>
            </div>

            <div style={full}>
              <div style={label}>Client Address *</div>
              <textarea
                rows={3}
                style={{ ...input, resize: "vertical" }}
                value={ep.clientAddress}
                onChange={(e) =>
                  setEp((s) => ({ ...s, clientAddress: e.target.value }))
                }
              />
            </div>

            <div style={full}>
              <div style={label}>Completion Date (optional)</div>
              <input
                type="date"
                style={input}
                value={ep.completionDate}
                onChange={(e) =>
                  setEp((s) => ({ ...s, completionDate: e.target.value }))
                }
              />
            </div>

            <div
              style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
            >
              <button onClick={() => setEditOpen(false)} style={btn(false)}>
                Cancel
              </button>

              <button
                onClick={saveEdit}
                disabled={editSaving}
                style={btn(true)}
              >
                {editSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
